import { useState, useEffect } from "react";
import { api, formatApiError } from "../../lib/api";
import { CHAT } from "../../constants/testIds";
import { X, Loader2, Brain, Sparkles, ArrowRight } from "lucide-react";
import confetti from "canvas-confetti";

export function FlashcardsModal({ open, onClose, conversationId }) {
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    const { data } = await api.get(`/flashcards?conversation_id=${conversationId}`);
    setCards(data);
    setIdx(0);
    setAnswer(""); setFeedback(null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) refresh(); }, [open, conversationId]);

  if (!open) return null;

  const generate = async (n = 8) => {
    setBusy(true); setErr(null);
    try {
      await api.post("/flashcards/generate", { conversation_id: conversationId, n_cards: n });
      await refresh();
    } catch (e) { setErr(formatApiError(e)); }
    setBusy(false);
  };

  const check = async () => {
    if (!cards[idx]) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/flashcards/answer", { card_id: cards[idx].id, user_answer: answer });
      setFeedback(data);
      if (data.verdict === "certo") {
        confetti({ particleCount: 50, spread: 60, colors: ["#7c3aed", "#a78bfa", "#34d399"] });
      }
    } catch (e) { setErr(formatApiError(e)); }
    setBusy(false);
  };

  const next = () => {
    setIdx((i) => (i + 1) % cards.length);
    setAnswer(""); setFeedback(null);
  };

  const card = cards[idx];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div data-testid={CHAT.flashcardsModal} className="w-full sm:max-w-lg bg-card border border-border rounded-t-3xl sm:rounded-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-violet-400" />
            <h2 className="font-bold tracking-tight">Flashcards</h2>
            {cards.length > 0 && <span className="text-xs text-muted-foreground">{idx + 1}/{cards.length}</span>}
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-thin">
          {cards.length === 0 && (
            <div className="text-center py-8 space-y-4">
              <Sparkles className="h-10 w-10 mx-auto text-violet-400/60" />
              <p className="text-sm text-muted-foreground">Sem flashcards nesta conversa. Gera uns rapidões?</p>
              <button
                data-testid={CHAT.flashcardsGenerate}
                onClick={() => generate(8)} disabled={busy}
                className="rounded-full bg-violet-600 hover:bg-violet-500 text-white px-5 py-2.5 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Gerar 8 flashcards
              </button>
            </div>
          )}

          {card && (
            <>
              <div className="rounded-2xl border border-border bg-secondary p-5 min-h-[120px]">
                <p className="text-[10px] uppercase tracking-wide text-violet-400 font-medium mb-2">Pergunta</p>
                <p className="text-base font-medium">{card.front}</p>
                {card.hint && <p className="text-xs text-muted-foreground mt-2 italic">Dica: {card.hint}</p>}
              </div>

              {!feedback && (
                <div className="space-y-2">
                  <textarea
                    data-testid={CHAT.flashcardAnswerInput}
                    value={answer} onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Escreve a resposta com suas palavras..."
                    rows={3}
                    className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50 resize-none scrollbar-thin"
                  />
                  <button
                    data-testid={CHAT.flashcardCheckButton}
                    onClick={check} disabled={busy || !answer.trim()}
                    className="w-full rounded-full bg-violet-600 hover:bg-violet-500 text-white py-2.5 text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Conferir
                  </button>
                </div>
              )}

              {feedback && (
                <div className={`rounded-xl border p-4 space-y-2 ${
                  feedback.verdict === "certo" ? "border-emerald-500/40 bg-emerald-500/10" :
                  feedback.verdict === "parcial" ? "border-amber-500/40 bg-amber-500/10" :
                  "border-red-500/40 bg-red-500/10"
                }`}>
                  <p className="text-xs uppercase tracking-wide font-semibold">
                    {feedback.verdict === "certo" ? "Certo!" : feedback.verdict === "parcial" ? "Parcialmente" : "Errou — sem stress"}
                  </p>
                  <p className="text-sm">{feedback.feedback}</p>
                  <div className="pt-2 border-t border-border/50 text-xs text-muted-foreground">
                    Resposta esperada: <span className="text-foreground">{card.answer}</span>
                  </div>
                  <button
                    data-testid={CHAT.flashcardNext}
                    onClick={next}
                    className="mt-2 w-full rounded-full bg-violet-600 hover:bg-violet-500 text-white py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2"
                  >
                    Próximo <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              )}

              <button
                onClick={() => generate(8)} disabled={busy}
                className="w-full text-xs text-muted-foreground hover:text-violet-400 py-2"
              >+ Gerar mais 8 cards</button>
            </>
          )}
          {err && <p className="text-sm text-red-400">{err}</p>}
        </div>
      </div>
    </div>
  );
}
